
import React, { useState, useEffect, useRef } from 'react';
import { MapZone, WeatherData } from '../types';
import { fetchDisasterMapData } from '../services/geminiService';
import { fetchTegalWeather } from '../services/weatherService';

const DisasterMap: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const userMarkerRef = useRef<any>(null);
  const userCircleRef = useRef<any>(null);
  
  const [selected, setSelected] = useState<MapZone | null>(null);
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [zones, setZones] = useState<MapZone[]>([]);
  const [isMapReady, setIsMapReady] = useState(false);
  const markersRef = useRef<any[]>([]);

  // Safely get Leaflet from window
  const getL = () => (window as any).L;

  useEffect(() => {
    const loadZones = async () => {
      setIsUpdating(true);
      try {
        const data = await fetchDisasterMapData();
        setZones(data);
      } finally {
        setIsUpdating(false);
      }
    };
    loadZones();

    // Auto-refresh every 30 seconds (30 * 1000)
    const intervalId = setInterval(() => {
      loadZones();
    }, 30 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const L = getL();
    if (!L) return;

    // Fetch Weather
    const getWeather = async () => {
      try {
        // Use OpenMeteo for map radar context
        const data = await fetchTegalWeather('Tegal');
        setWeather(data);
      } catch (e) {
        console.error("Failed to fetch weather", e);
      }
    };
    getWeather();

    // Initialize Leaflet Map
    if (mapContainerRef.current && !mapInstanceRef.current) {
      const map = L.map(mapContainerRef.current, {
        zoomControl: false,
        attributionControl: true
      }).setView([-6.95, 109.13], 11);

      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; OSM & CARTO',
        subdomains: 'abcd',
        maxZoom: 20
      }).addTo(map);

      L.control.zoom({ position: 'topleft' }).addTo(map);

      mapInstanceRef.current = map;
      setIsMapReady(true);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers when zones change
  useEffect(() => {
    const L = getL();
    const map = mapInstanceRef.current;
    
    if (!map || !L || !isMapReady) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add new markers
    if (Array.isArray(zones)) {
      zones.forEach((zone) => {
        const pinColor = zone.risk === 'High' ? '#ef4444' : '#f59e0b';
        
        const customIcon = L.divIcon({
          className: 'custom-div-icon',
          html: `
            <div style="filter: drop-shadow(0 4px 6px rgb(0 0 0 / 0.15));">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 0C7.58 0 4 3.58 4 8C4 13.5 12 24 12 24S20 13.5 20 8C20 3.58 16.42 0 12 0Z" fill="${pinColor}" stroke="white" stroke-width="2"/>
                <circle cx="12" cy="8" r="3" fill="white"/>
              </svg>
            </div>
          `,
          iconSize: [32, 32],
          iconAnchor: [16, 32]
        });

        const marker = L.marker([zone.coords.lat, zone.coords.lng], { icon: customIcon }).addTo(map);
        
        marker.on('click', () => {
          setSelected(zone);
          const offsetLat = window.innerWidth < 640 ? zone.coords.lat - 0.02 : zone.coords.lat;
          map.flyTo([offsetLat, zone.coords.lng], 13, { duration: 1.5 });
        });
        
        markersRef.current.push(marker);
      });
    }
  }, [zones, isMapReady]);

  const handleLocateUser = () => {
    const L = getL();
    if (!L || !navigator.geolocation) {
      alert("Geolokasi tidak didukung atau belum siap.");
      return;
    }

    setIsLocating(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const map = mapInstanceRef.current;

        if (map) {
          if (userMarkerRef.current) map.removeLayer(userMarkerRef.current);
          if (userCircleRef.current) map.removeLayer(userCircleRef.current);

          const userIcon = L.divIcon({
            className: 'user-location-icon',
            html: `
              <div class="flex relative justify-center items-center">
                <div class="absolute w-10 h-10 bg-blue-500 rounded-full opacity-25 animate-ping"></div>
                <div class="relative w-5 h-5 bg-blue-600 rounded-full border-[3px] border-white shadow-xl"></div>
              </div>
            `,
            iconSize: [40, 40],
            iconAnchor: [20, 20]
          });

          userMarkerRef.current = L.marker([latitude, longitude], { icon: userIcon }).addTo(map);
          userCircleRef.current = L.circle([latitude, longitude], {
            radius: accuracy,
            color: '#3b82f6',
            fillColor: '#3b82f6',
            fillOpacity: 0.1,
            weight: 1
          }).addTo(map);

          const offsetLat = window.innerWidth < 640 ? latitude - 0.01 : latitude;
          map.flyTo([offsetLat, longitude], 15, { duration: 2 });
        }
        setIsLocating(false);
      },
      (error) => {
        console.error("Error identifying location:", error);
        alert("Gagal mendapatkan lokasi. Pastikan izin lokasi aktif.");
        setIsLocating(false);
      },
      { enableHighAccuracy: true }
    );
  };

  return (
    <div className="bg-white rounded-[2rem] p-3 sm:p-5 shadow-xl border border-gray-100 overflow-hidden relative">
      <div className="flex flex-wrap gap-2 justify-between items-center mb-4">
        <div className="flex gap-3 items-center">
          <div className="p-2.5 bg-blue-50 rounded-2xl shadow-sm border border-blue-100">
            <span className="text-xl">🗺️</span>
          </div>
          <div>
            <div className="flex gap-2 items-center">
              <h3 className="text-base font-bold leading-none text-gray-900 sm:text-lg">Radar Siaga Tegal</h3>
              {isUpdating && (
                 <span className="flex relative w-2 h-2">
                   <span className="inline-flex absolute w-full h-full bg-blue-400 rounded-full opacity-75 animate-ping"></span>
                   <span className="inline-flex relative w-2 h-2 bg-blue-500 rounded-full"></span>
                 </span>
              )}
            </div>
            <p className="text-[10px] sm:text-[11px] text-gray-400 mt-1 font-bold uppercase tracking-wider flex items-center gap-1">
                Pemantauan Langsung <span className="text-gray-300">•</span> 
                {isUpdating ? (
                  <span className="text-blue-500 animate-pulse">Sedang mencari informasi...</span>
                ) : (
                  "Update tiap 30 detik"
                )}
              </p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
           <div className="bg-green-50 px-2 py-1 rounded-lg border border-green-100 text-[8px] font-black text-green-700 uppercase tracking-widest hidden xs:block">
            LIVE MAP
          </div>
        </div>
      </div>
      
      <div className="relative w-full h-[350px] sm:h-[450px] bg-gray-100 rounded-3xl overflow-hidden border border-gray-200 shadow-inner">
        <div ref={mapContainerRef} className="z-0 w-full h-full" />

        <button 
          onClick={handleLocateUser}
          disabled={isLocating}
          className={`absolute top-4 right-4 z-[400] p-4 bg-white rounded-2xl shadow-2xl border border-gray-100 transition-all active:scale-90 hover:bg-gray-50 group ${isLocating ? 'opacity-50' : ''}`}
        >
          <span className={`text-xl block ${isLocating ? 'animate-spin' : 'group-hover:scale-110'}`}>
            {isLocating ? '⏳' : '🎯'}
          </span>
        </button>

        {!selected && weather && (
          <div className="absolute top-4 left-16 sm:left-auto sm:bottom-4 sm:right-4 pointer-events-none animate-in fade-in slide-in-from-top-4 sm:slide-in-from-right-4 duration-500 z-[400]">
            <div className="bg-white/95 backdrop-blur-md p-2.5 sm:p-3 rounded-2xl shadow-xl border border-gray-100 flex items-center sm:flex-col gap-3 sm:gap-1 pointer-events-auto">
               <div className="flex gap-2 items-center">
                 <span className="text-xl sm:text-2xl">{weather.icon}</span>
                 <span className="text-xs font-black tracking-tighter text-gray-800 sm:text-sm">{weather.temp}</span>
               </div>
               <div className="hidden my-1 w-full h-px bg-gray-100 sm:block"></div>
               <div className="flex gap-3 px-1 sm:gap-4">
                 <div className="flex flex-col items-center">
                   <span className="text-[6px] sm:text-[7px] text-gray-400 uppercase font-black tracking-tighter">Hujan</span>
                   <span className="text-[9px] sm:text-[10px] font-bold text-blue-600">{weather.rainChance}</span>
                 </div>
                 <div className="flex hidden flex-col items-center xs:flex">
                   <span className="text-[6px] sm:text-[7px] text-gray-400 uppercase font-black tracking-tighter">Lembab</span>
                   <span className="text-[9px] sm:text-[10px] font-bold text-blue-500">{weather.humidity}</span>
                 </div>
               </div>
            </div>
          </div>
        )}

        {selected && (
          <div className="absolute inset-x-0 bottom-0 bg-white shadow-[0_-20px_50px_rgba(0,0,0,0.2)] rounded-t-[3rem] p-5 sm:p-8 animate-in slide-in-from-bottom-full duration-500 z-[500] max-h-[85%] overflow-y-auto">
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mb-6 sticky top-0"></div>
            
            <div className="flex justify-between items-start mb-6">
              <div className="flex gap-4">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center text-3xl sm:text-4xl shadow-sm shrink-0 ${
                  selected.type === 'Rob' ? 'bg-blue-50' : 
                  selected.type === 'Longsor' ? 'bg-orange-50' : 'bg-red-50'
                }`}>
                  {selected.type === 'Rob' ? '🌊' : selected.type === 'Longsor' ? '⛰️' : '🌧️'}
                </div>
                <div>
                  <h4 className="text-lg font-black leading-tight text-gray-900 sm:text-2xl">{selected.name}</h4>
                  <div className="flex flex-wrap gap-2 items-center mt-2">
                    <span className={`flex items-center gap-1.5 text-[9px] sm:text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider ${
                      selected.risk === 'High' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'
                    }`}>
                      <span className="w-2 h-2 bg-current rounded-full animate-pulse"></span>
                      Resiko {selected.risk}
                    </span>
                    <span className="text-[10px] text-gray-300 font-bold">•</span>
                    <span className="text-[9px] sm:text-[10px] text-gray-500 font-black uppercase tracking-widest">{selected.type}</span>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setSelected(null)}
                className="flex justify-center items-center w-10 h-10 text-gray-400 bg-gray-100 rounded-full transition-all hover:bg-gray-200 active:scale-90"
              >
                ✕
              </button>
            </div>

            <div className="space-y-5">
              <div className="p-5 rounded-3xl border border-gray-100 bg-gray-50/80">
                {selected.description ? (
                  <>
                    <div className="flex gap-2 items-center mb-3">
                      <span className="text-lg">ℹ️</span>
                      <p className="text-[11px] font-black text-gray-800 uppercase tracking-widest">Keterangan Kejadian</p>
                    </div>
                    <p className="mb-4 text-sm font-medium leading-relaxed text-gray-600">
                      {selected.description}
                    </p>
                  </>
                ) : null}

                <div className="flex gap-2 items-center mb-3">
                   <span className="text-lg">🛡️</span>
                   <p className="text-[11px] font-black text-gray-800 uppercase tracking-widest">Panduan Keselamatan</p>
                </div>
                <p className="text-sm font-medium leading-relaxed text-gray-600">
                  {selected.type === 'Rob' ? 'Pantau pasang surut air laut di Pelabuhan Tegal. Amankan barang ke tempat tinggi dan siapkan cadangan air bersih.' : 
                   selected.type === 'Longsor' ? 'Waspadai retakan di lereng bukit. Jika hujan lebat terjadi lebih dari 3 jam, segera evakuasi ke titik kumpul aman.' : 
                   'Waspadai kenaikan debit sungai Pemali atau Ketiwon. Segera matikan aliran listrik di rumah jika air mulai masuk.'}
                </p>
              </div>

              {(selected.referenceUrls && selected.referenceUrls.length > 0) || selected.referenceUrl ? (
                <div className="mb-4">
                  <div className="flex gap-2 items-center mb-2">
                    <span className="text-lg">🔗</span>
                    <p className="text-[11px] font-black text-gray-800 uppercase tracking-widest">Referensi / Berita</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {/* Fallback for legacy referenceUrl */}
                    {selected.referenceUrl && !selected.referenceUrls && (
                       <a 
                        href={selected.referenceUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex gap-2 items-center p-3 text-xs font-bold text-blue-600 bg-blue-50 rounded-xl border border-blue-100 transition-colors hover:text-blue-700 hover:bg-blue-100"
                      >
                        <span className="flex-1 truncate">Baca Sumber Berita</span>
                        <span className="text-xs">↗</span>
                      </a>
                    )}
                    
                    {/* New multiple referenceUrls */}
                    {selected.referenceUrls?.map((url, idx) => (
                      <a 
                        key={idx}
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex gap-2 items-center p-3 text-xs font-bold text-blue-600 bg-blue-50 rounded-xl border border-blue-100 transition-colors hover:text-blue-700 hover:bg-blue-100"
                      >
                        <span className="flex-1 truncate">Sumber {idx + 1}: {new URL(url).hostname.replace('www.', '')}</span>
                        <span className="text-xs">↗</span>
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-3 pb-4">
                <button 
                  onClick={() => window.open(`https://www.openstreetmap.org/?mlat=${selected.coords.lat}&mlon=${selected.coords.lng}#map=16/${selected.coords.lat}/${selected.coords.lng}`, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-14 rounded-2xl font-black text-[11px] uppercase flex items-center justify-center gap-2 shadow-xl shadow-blue-200 transition-all active:scale-95"
                >
                  <span>🗺️</span>
                  <span>Detail</span>
                </button>
                <button 
                  onClick={() => window.open('tel:112')}
                  className="bg-red-600 hover:bg-red-700 text-white h-14 rounded-2xl font-black text-[11px] uppercase flex items-center justify-center gap-2 shadow-xl shadow-red-200 transition-all active:scale-95"
                >
                   <span>📞</span>
                   <span>112</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 justify-between items-start mt-5 xs:flex-row xs:items-center">
         <div className="flex flex-col">
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
              Sumber Data
            </p>
            <p className="text-[11px] text-gray-900 font-black tracking-tight">
              BPBD Kota & Kab. Tegal
            </p>
         </div>
        <div className="flex gap-4 items-center px-4 py-2 bg-gray-50 rounded-xl border border-gray-100">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
              <span className="text-[10px] font-black text-gray-600 uppercase">Tinggi</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
              <span className="text-[10px] font-black text-gray-600 uppercase">Sedang</span>
            </div>
        </div>
      </div>
    </div>
  );
};

export default DisasterMap;
